// Single source of truth for action-button sizing across the portal.
//
// Page toolbars, card headers and modal footers all use these — so "New Screen",
// "New Playlist", "Upload", "Save" and "Cancel" end up the same height and radius
// everywhere. Small inline pills inside table rows (fontSize 10–11) are a separate,
// deliberate tier and are not covered here.

const btnBaseSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  px: '12px',
  py: '6px',
  borderRadius: '8px',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  transition: 'all .15s',
  // transparent border on every variant, so bordered and borderless buttons
  // still line up at the same height
  border: '1px solid transparent',
} as const;

/** Main action of a page or modal: filled with the accent colour. */
export const btnPrimarySx = {
  ...btnBaseSx,
  background: 'var(--accent)',
  color: '#fff',
  '&:hover': { opacity: .88 },
} as const;

/** Same weight as primary but with the accent gradient — for save/publish. */
export const btnPrimaryGradientSx = {
  ...btnBaseSx,
  background: 'linear-gradient(135deg, var(--accent), var(--accent-light))',
  color: '#fff',
  boxShadow: '0 2px 8px var(--accent-glow)',
  '&:hover': { opacity: .9 },
} as const;

/** Everything next to a primary action: glass surface, muted text. */
export const btnSecondarySx = {
  ...btnBaseSx,
  background: 'var(--glass-bg)',
  borderColor: 'var(--glass-border)',
  color: 'var(--text-secondary)',
  '&:hover': { background: 'var(--glass-hover)', color: 'var(--text-primary)' },
} as const;

/** Destructive action. */
export const btnDangerSx = {
  ...btnBaseSx,
  background: 'rgba(248,113,113,.08)',
  borderColor: 'rgba(248,113,113,.25)',
  color: '#f87171',
  '&:hover': { background: 'rgba(248,113,113,.16)', color: '#fca5a5' },
} as const;

/** Icon-only button, same height as the text buttons above. */
export const btnIconSx = {
  ...btnBaseSx,
  px: 0,
  width: 30,
  height: 30,
  background: 'var(--glass-bg)',
  borderColor: 'var(--glass-border)',
  color: 'var(--text-secondary)',
  '&:hover': { background: 'var(--glass-hover)', color: 'var(--text-primary)', borderColor: 'var(--glass-border-strong)' },
} as const;

/** Disabled look, spread after a variant. */
export const btnDisabledSx = {
  cursor: 'default',
  opacity: .55,
} as const;
